import type { FormQuestion, SignatureAnswerValue } from '@kushya/shared';

type SignatureQuestion = Extract<FormQuestion, { type: 'signature' }>;

export function normalizeSignatureAnswer(value: unknown): SignatureAnswerValue | null {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    if (trimmed.startsWith('http')) {
      return { url: trimmed };
    }

    return null;
  }

  if (typeof value === 'object' && value !== null) {
    const record = value as SignatureAnswerValue;

    if (typeof record.url === 'string' && record.url.trim()) {
      const normalized: SignatureAnswerValue = {
        url: record.url.trim(),
      };

      if (typeof record.typedName === 'string' && record.typedName.trim()) {
        normalized.typedName = record.typedName;
      }

      if (typeof record.uploadedAt === 'string') {
        normalized.uploadedAt = record.uploadedAt;
      }

      return normalized;
    }
  }

  return null;
}

export function hasSignatureArtifact(value: unknown): boolean {
  return Boolean(normalizeSignatureAnswer(value)?.url);
}

export function isSignatureAnswerComplete(question: SignatureQuestion, value: unknown): boolean {
  const normalized = normalizeSignatureAnswer(value);
  if (!normalized?.url) {
    return false;
  }

  if (question.requireTypedName) {
    return Boolean(normalized.typedName && normalized.typedName.trim());
  }

  return true;
}

export function isTypedNameMissing(question: SignatureQuestion, value: unknown): boolean {
  if (!question.requireTypedName) {
    return false;
  }

  const normalized = normalizeSignatureAnswer(value);
  if (!normalized?.url) {
    return false;
  }

  return !(normalized.typedName && normalized.typedName.trim());
}
